import React, { useState } from 'react';

import Lottie from 'lottie-react-web';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton, { ActionButtonVariants } from 'components/action-button';

import loadingAnimation from './animations/map_dots.json';
import initialAnimation from './animations/map_pin_drop.json';
import transitionAnimation from './animations/map_pin_recede.json';

const Button = styled(ActionButton)`
  width: 48px;
  height: 48px;
  padding: 8px;

  path:not([fill='rgb(255,255,255)']) {
    fill: ${Styles.color.primary};
  }

  &.loading {
    padding: 0;
  }
`;

const LoadingAnimation: React.FC = () => (
  <Lottie
    ariaRole="presentation"
    data-testid="reset-location-loading-animation"
    options={{ animationData: loadingAnimation }}
  />
);

const TransitionAnimation: React.FC<{ onComplete: () => void }> = ({ onComplete }) => (
  <Lottie
    ariaRole="presentation"
    data-testid="reset-location-transition-animation"
    options={{
      animationData: transitionAnimation,
      loop: false,
    }}
    eventListeners={[
      {
        eventName: 'complete',
        callback: onComplete,
      },
    ]}
  />
);

const InitialAnimation: React.FC = () => (
  <Lottie
    ariaRole="presentation"
    data-testid="reset-location-initial-animation"
    options={{ animationData: initialAnimation, loop: false }}
  />
);

interface ResetLocationButtonProps {
  onClick: () => void;
  loading?: boolean;
}

const ResetLocationButton: React.FC<ResetLocationButtonProps> = ({ onClick, loading }) => {
  const [status, setStatus] = useState<'idle' | 'transitioning'>('idle');
  const { formatMessage } = useIntl();

  const handleClick = () => {
    setStatus('transitioning');
  };

  const getStartIcon = () => {
    if (loading) {
      return <LoadingAnimation />;
    }

    if (status === 'transitioning') {
      return (
        <TransitionAnimation
          onComplete={() => {
            onClick();
            setStatus('idle');
          }}
        />
      );
    }

    return <InitialAnimation />;
  };

  return (
    <Button
      aria-label={formatMessage({ id: 'resetLocationButtonAria' })}
      data-testid="reset-location-button"
      onClick={handleClick}
      variant={ActionButtonVariants.BOX_SHADOW_INVERSE}
      startIcon={getStartIcon()}
      disabled={loading}
      className={`${loading ? 'loading' : ''}`}
    />
  );
};

export default ResetLocationButton;
